/**
 * Markandeya Puranam — load canonical JSON + contributor stories (API + localStorage).
 */
import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();
const PURANA_ID = "markandeya-puranam";
const STATIC_URL = `/content/puranas/${PURANA_ID}.json`;
const STORAGE_KEY = `vedapath_${PURANA_ID}_user_stories`;

function slugify(text) {
  return String(text)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "")
    .slice(0, 48);
}

export function isStoriesIncomplete(data) {
  if (!data) return true;
  const expected = data.expected_story_count ?? 0;
  const count = data.stories?.length ?? 0;
  return expected <= 0 || count < expected;
}

export function mergePuranaStories(canonical, userStories = []) {
  const base = Array.isArray(canonical?.stories) ? canonical.stories : [];
  const seen = new Set(base.map((s) => s.id));
  const merged = [...base];
  for (const story of userStories) {
    if (!story?.id || seen.has(story.id)) continue;
    seen.add(story.id);
    merged.push(story);
  }
  merged.sort((a, b) => (a.order ?? 999) - (b.order ?? 999));
  return { ...canonical, stories: merged };
}

function readLocalUserStories() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeLocalUserStories(stories) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stories));
}

async function fetchUserStoriesFromApi() {
  try {
    const res = await fetch(`${API}/content/puranas/${PURANA_ID}/user-stories`);
    if (!res.ok) return null;
    const data = await res.json();
    return Array.isArray(data.stories) ? data.stories : [];
  } catch {
    return null;
  }
}

async function fetchCanonicalPurana() {
  try {
    const apiRes = await fetch(`${API}/content/puranas/${PURANA_ID}`);
    if (apiRes.ok) return apiRes.json();
  } catch {
    /* fall through to static JSON */
  }
  const staticRes = await fetch(STATIC_URL);
  if (!staticRes.ok) throw new Error("Markandeya Puranam content unavailable");
  return staticRes.json();
}

export async function loadMarkandeyaPuranam() {
  const canonical = await fetchCanonicalPurana();

  const fromApi = await fetchUserStoriesFromApi();
  const userStories = fromApi ?? readLocalUserStories();
  if (fromApi) writeLocalUserStories(fromApi);

  return mergePuranaStories(canonical, userStories);
}

export function parseContributorStories(rawText) {
  const blocks = String(rawText)
    .split(/\n\s*\n/)
    .map((b) => b.trim())
    .filter(Boolean);

  return blocks.map((block, index) => {
    const lines = block.split("\n").map((l) => l.trim()).filter(Boolean);
    const title = lines[0] ?? `Story ${index + 1}`;
    const summary = lines.slice(1).join(" ").trim() || title;
    const id = `contributor_${slugify(title)}_${Date.now()}_${index}`;
    return {
      id,
      title,
      summary,
      teaching: "",
      characters: [],
      order: 900 + index,
      source: "contributor",
    };
  });
}

export async function saveContributorStories(newStories) {
  if (!Array.isArray(newStories) || newStories.length === 0) {
    return { ok: false, error: "No stories to save" };
  }

  const existing = readLocalUserStories();
  const merged = [...existing];
  const seen = new Set(existing.map((s) => s.id));
  for (const story of newStories) {
    if (!story?.title?.trim()) continue;
    const id = story.id ?? `contributor_${slugify(story.title)}_${Date.now()}`;
    if (seen.has(id)) continue;
    seen.add(id);
    merged.push({ ...story, id, source: "contributor" });
  }
  writeLocalUserStories(merged);

  try {
    const res = await fetch(`${API}/content/puranas/${PURANA_ID}/user-stories`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ stories: newStories }),
    });
    if (res.ok) {
      const data = await res.json();
      if (Array.isArray(data.stories)) writeLocalUserStories(data.stories);
      return { ok: true, stories: data.stories ?? merged };
    }
  } catch {
    /* localStorage already updated */
  }

  return { ok: true, stories: merged, persistedLocally: true };
}

export async function saveContributorStory(story) {
  return saveContributorStories([story]);
}
